import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { ShieldAlert, ExternalLink, RefreshCw } from "lucide-react";

const DOC_META = {
  terms:            { label: "Terms of Service",  path: "/terms",            userField: "terms_version" },
  privacy:          { label: "Privacy Policy",    path: "/privacy",          userField: "privacy_version" },
  commercial_terms: { label: "Commercial Terms",  path: "/commercial-terms", userField: "commercial_terms_version" },
};

/**
 * Blocking modal shown to logged-in users when any document version they
 * previously accepted is older than the current server-side version. Users
 * cannot dismiss it — they must accept the updated documents to continue.
 *
 * Skipped for users who have never accepted a document (no stored version),
 * so the modal never appears mid-onboarding for the very first signup step.
 */
export default function LegalUpdateGate() {
  const { user, refresh } = useAuth();
  const [versions, setVersions] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/legal/versions").then(({ data }) => setVersions(data)).catch(() => {});
  }, [user]);

  if (!user || !versions) return null;

  // Only flag a mismatch when the user has previously accepted this document.
  // Missing field = never accepted = not a "mismatch", not a re-acceptance.
  const outdated = Object.entries(DOC_META)
    .filter(([type, meta]) => {
      const accepted = user[meta.userField];
      return accepted && accepted !== versions[type];
    })
    .map(([type, meta]) => ({ type, ...meta, current: versions[type], previous: user[meta.userField] }));

  if (outdated.length === 0) return null;

  const accept = async () => {
    setSubmitting(true); setError("");
    try {
      await api.post("/legal/accept", { document_types: outdated.map((o) => o.type) });
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="legal-update-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-update-title"
    >
      <div className="bg-bg-surface border border-volt w-full max-w-xl">
        <div className="px-6 py-5 border-b border-line flex items-start gap-3">
          <div className="w-10 h-10 bg-volt/10 border border-volt/40 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-volt" />
          </div>
          <div>
            <div className="label-eyebrow">Action required</div>
            <h2 id="legal-update-title" className="font-heading text-2xl uppercase tracking-tight mt-1">
              Updated Terms
            </h2>
          </div>
        </div>

        <div className="px-6 py-6 space-y-5">
          <p className="text-sm text-ink-muted leading-relaxed">
            We've updated our legal terms. Please review and accept the latest version to continue using
            PITCHD.
          </p>

          <ul className="space-y-2" data-testid="legal-update-list">
            {outdated.map((o) => (
              <li key={o.type} className="border border-line p-3 flex items-center justify-between gap-3" data-testid={`legal-update-${o.type}`}>
                <div className="min-w-0">
                  <div className="font-heading text-base uppercase tracking-tight">{o.label}</div>
                  <div className="text-xs text-ink-muted font-mono mt-0.5">
                    Your version: {o.previous} → New version: <span className="text-volt">{o.current}</span>
                  </div>
                </div>
                <a
                  href={o.path}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs uppercase tracking-widest text-volt inline-flex items-center gap-1 flex-shrink-0 hover:underline"
                  data-testid={`legal-update-${o.type}-link`}
                >
                  Review <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>

          {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 p-3" data-testid="legal-update-error">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t border-line flex justify-end">
          <button
            onClick={accept}
            disabled={submitting}
            className="btn-volt inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="legal-update-accept-btn"
          >
            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
            {submitting ? "Saving…" : `Accept ${outdated.length === 1 ? "update" : "updates"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
